import React, { useState, useEffect } from 'react';
import { Lock, ArrowLeft } from 'lucide-react';

const MultiWebsiteLogin = ({ 
  websiteConfig, 
  onLogin, 
  onBackToSite, 
  apiClient,
  isAuthenticated = false 
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if already authenticated on component mount
    if (apiClient && apiClient.isLoggedIn()) {
      onLogin(apiClient.user);
    }
  }, [apiClient, onLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await apiClient.login(username, password);
      
      if (result.success) {
        onLogin(result.user);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return null; // Don't render if already authenticated
  }

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${websiteConfig.backgroundClass}`}>
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="flex items-center justify-center mb-6">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${websiteConfig.iconBgClass}`}>
            {websiteConfig.icon || <Lock className={`w-6 h-6 ${websiteConfig.iconColorClass}`} />}
          </div>
        </div>
        
        <div className="text-center mb-8">
          <h1 className={`text-2xl font-bold mb-2 ${websiteConfig.titleColorClass}`}>
            {websiteConfig.title}
          </h1>
          <p className="text-gray-600">
            {websiteConfig.subtitle}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-colors"
              style={{ '--tw-ring-color': websiteConfig.focusColor }}
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-colors"
              style={{ '--tw-ring-color': websiteConfig.focusColor }}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${websiteConfig.buttonClass}`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Signing in...
              </div>
            ) : (
              'Sign in'
            )}
          </button>
        </form>

        {websiteConfig.demoCredentials && (
          <div className={`mt-6 p-4 rounded-lg border ${websiteConfig.demoBgClass} ${websiteConfig.demoBorderClass}`}>
            <h4 className={`text-sm font-medium mb-2 ${websiteConfig.demoTextClass}`}>Demo Credentials:</h4>
            <div className={`text-sm ${websiteConfig.demoContentClass}`}>
              <p><strong>Username:</strong> {websiteConfig.demoCredentials.username}</p>
              <p><strong>Password:</strong> {websiteConfig.demoCredentials.password}</p>
            </div>
          </div>
        )}

        {onBackToSite && (
          <div className="mt-6 text-center">
            <button
              onClick={onBackToSite}
              className={`flex items-center gap-2 mx-auto transition-colors ${websiteConfig.backLinkClass}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Website
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiWebsiteLogin;